import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { Match, Client, Property, MatchScore, MatchStatus, ApiResponse, PaginationInfo } from '@/types';
import { PropertyService } from './propertyService';
import { AuthService } from './auth';

export class MatchingService {
  private static collection = 'matches';


  // Find matching clients for a property
  static async findMatchingClients(property: Property): Promise<{ client: Client; score: MatchScore }[]> {
    try {
      // Get all active clients
      // Note: In a real app with many clients, this should be done via a Cloud Function
      // or a more optimized query (e.g., filtering by price range first)
      const q = query(
        collection(db, 'clients'),
        where('status', '==', 'active')
      );
      
      const querySnapshot = await getDocs(q);
      const clients = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Client[];

      const matches: { client: Client; score: MatchScore }[] = [];

      for (const client of clients) {
        // Skip if client doesn't have preferences set
        if (!client.preferences) continue;

        const score = this.calculateMatchScore(client, property);
        
        // Only include if score is reasonable (e.g. > 50)
        if (score.overall >= 50) {
          matches.push({ client, score });
        }
      }

      // Sort by score descending
      return matches.sort((a, b) => b.score.overall - a.score.overall);
    } catch (error) {
      console.error('Failed to find matching clients:', error);
      return [];
    }
  }

  // Generate matches for a client
  static async generateMatchesForClient(client: Client): Promise<Match[]> {
    try {
      const clientId = client.id;

      // Get all active properties
      const propertiesResponse = await PropertyService.getProperties({ isActive: true });
      if (!propertiesResponse.success || !propertiesResponse.data) {
        throw new Error('Failed to get properties');
      }

      const properties = propertiesResponse.data;
      
      // Get existing matches to avoid duplicates
      const existingMatches = await this.getMatchesForClient(clientId);
      const matchMap = new Map(existingMatches.map(m => [m.propertyId, m]));
      
      const matches: Match[] = [];

      // Calculate match scores for each property
      for (const property of properties) {
        const score = this.calculateMatchScore(client, property);
        
        // Only create/update matches with score above threshold (e.g., 60%)
        if (score.overall >= 60) {
          const existingMatch = matchMap.get(property.id);
          
          if (existingMatch) {
            // Update existing match
            const updates: Partial<Match> = {
              score,
              updatedAt: new Date() as any,
            };
            
            await updateDoc(doc(db, this.collection, existingMatch.id), updates);
            matches.push({ ...existingMatch, ...updates } as Match);
          } else {
            // Create new match
            const match: Omit<Match, 'id'> = {
              clientId,
              propertyId: property.id,
              agentId: client.agentId,
              score,
              status: MatchStatus.PENDING,
              analytics: {
                clickThroughRate: 0,
                conversionRate: 0,
              },
              createdAt: new Date() as any,
              updatedAt: new Date() as any,
            };

            const docRef = await addDoc(collection(db, this.collection), match);
            matches.push({ ...match, id: docRef.id });
          }
        } else {
          // If score is low but match exists, update it to reflect lower score
          const existingMatch = matchMap.get(property.id);
          if (existingMatch) {
             const updates: Partial<Match> = {
              score,
              updatedAt: new Date() as any,
            };
            await updateDoc(doc(db, this.collection, existingMatch.id), updates);
          }
        }
      }

      return matches;
    } catch (error) {
      throw new Error(`Failed to generate matches: ${error}`);
    }
  }

  // Calculate match score between client and property
  public static calculateMatchScore(client: Client, property: Property): MatchScore {
    const { preferences, matchingProfile } = client;
    const { metadata, features, location } = property;

    let totalScore = 0;
    let maxScore = 0;
    const strengths: string[] = [];
    const weaknesses: string[] = [];

    // Determine weights
    let weights = {
      price: 30,
      location: 25,
      type: 20,
      features: 15,
      timeline: 10
    };

    // If client has priority settings, use them to adjust weights
    if (matchingProfile?.priority) {
      const { price, location, features, timeline } = matchingProfile.priority;
      const sum = price + location + features + timeline;
      
      if (sum > 0) {
        const factor = 100 / sum;
        weights.price = price * factor;
        weights.location = location * factor;
        weights.timeline = timeline * factor;
        
        // Split features priority between type and actual features based on default ratio (20:15)
        const combinedFeaturesWeight = features * factor;
        const typeRatio = 20 / 35;
        const featuresRatio = 15 / 35;
        
        weights.type = combinedFeaturesWeight * typeRatio;
        weights.features = combinedFeaturesWeight * featuresRatio;
      }
    }

    // Price matching
    maxScore += weights.price;
    const priceScore = this.calculatePriceScore(preferences.priceRange, metadata.price);
    totalScore += (priceScore * weights.price) / 100;
    
    if (priceScore >= 80) strengths.push('Great price match');
    else if (priceScore < 50) weaknesses.push('Price may be outside budget');

    // Location matching
    maxScore += weights.location;
    const locationScore = this.calculateLocationScore(preferences.location, location);
    totalScore += (locationScore * weights.location) / 100;
    
    if (locationScore >= 80) strengths.push('Perfect location match');
    else if (locationScore < 50) weaknesses.push('Location may not meet preferences');

    // Property type matching
    maxScore += weights.type;
    const typeScore = this.calculateTypeScore(preferences.propertyTypes, metadata.propertyType);
    totalScore += (typeScore * weights.type) / 100;
    
    if (typeScore >= 80) strengths.push('Preferred property type');
    else if (typeScore < 50) weaknesses.push('Not preferred property type');

    // Features matching
    maxScore += weights.features;
    const featuresScore = this.calculateFeaturesScore(preferences.features, features, metadata);
    totalScore += (featuresScore * weights.features) / 100;
    
    if (featuresScore >= 80) strengths.push('Has desired features');
    else if (featuresScore < 50) weaknesses.push('Missing key features');

    // Timeline matching
    maxScore += weights.timeline;
    const timelineScore = this.calculateTimelineScore(preferences.timeline);
    totalScore += (timelineScore * weights.timeline) / 100;
    
    if (timelineScore >= 80) strengths.push('Timeline alignment');
    else if (timelineScore < 50) weaknesses.push('Timeline mismatch');

    const overallScore = Math.round((totalScore / maxScore) * 100);

    return {
      overall: overallScore,
      price: Math.round(priceScore),
      location: Math.round(locationScore),
      features: Math.round(featuresScore),
      timeline: Math.round(timelineScore),
      quality: Math.round((overallScore + priceScore + locationScore + featuresScore) / 4),
      explanation: this.generateMatchExplanation(overallScore, strengths, weaknesses),
      strengths,
      weaknesses,
    };
  }

  // Calculate price match score
  private static calculatePriceScore(priceRange: Client['preferences']['priceRange'], price: number): number {
    const { min, max } = priceRange;
    
    if (price >= min && price <= max) {
      // Perfect match within budget
      return 100;
    } else if (price < min) {
      // Below budget - good but not perfect
      return Math.max(60, 100 - ((min - price) / min) * 40);
    } else {
      // Above budget - calculate penalty
      const overage = price - max;
      const overagePercentage = (overage / max) * 100;
      return Math.max(0, 100 - overagePercentage * 2);
    }
  }

  // Calculate location match score
  private static calculateLocationScore(
    locationPrefs: Client['preferences']['location'],
    propertyLocation: Property['location']
  ): number {
    let score = 0;
    let factors = 0;

    // City match
    if (locationPrefs.cities.includes(propertyLocation.city)) {
      score += 40;
    }
    factors += 40;

    // Neighborhood match
    if (locationPrefs.neighborhoods.includes(propertyLocation.neighborhood || '')) {
      score += 30;
    }
    factors += 30;

    // School district match
    if (locationPrefs.schoolDistricts?.includes(propertyLocation.schoolDistrict || '')) {
      score += 20;
    }
    factors += 20;

    // Walk score match
    if (locationPrefs.walkScore && propertyLocation.walkScore) {
      const walkScoreDiff = Math.abs(locationPrefs.walkScore - propertyLocation.walkScore);
      score += Math.max(0, 10 - walkScoreDiff / 10);
    }
    factors += 10;

    return factors > 0 ? (score / factors) * 100 : 50; // Default to 50 if no preferences set
  }

  // Calculate property type match score
  private static calculateTypeScore(
    preferredTypes: Client['preferences']['propertyTypes'],
    propertyType: Property['metadata']['propertyType']
  ): number {
    if (preferredTypes.includes(propertyType)) {
      return 100;
    }
    
    // Check for similar types
    const similarTypes: { [key: string]: string[] } = {
      'house': ['villa', 'duplex'],
      'apartment': ['condo', 'studio', 'loft'],
      'condo': ['apartment', 'studio', 'loft'],
      'townhouse': ['house', 'duplex'],
    };

    const similar = similarTypes[propertyType] || [];
    if (preferredTypes.some(type => similar.includes(type))) {
      return 70;
    }

    return 30; // Low score for non-preferred types
  }

  // Calculate features match score
  private static calculateFeaturesScore(
    featurePrefs: Client['preferences']['features'],
    propertyFeatures: Property['features'],
    metadata: Property['metadata']
  ): number {
    let score = 0;
    let totalChecks = 0;

    // Bedroom count
    const bedroomMatch = this.checkRangeMatch(
      featurePrefs.bedrooms,
      metadata.bedrooms
    );
    score += bedroomMatch * 20;
    totalChecks += 20;

    // Bathroom count
    const bathroomMatch = this.checkRangeMatch(
      featurePrefs.bathrooms,
      metadata.bathrooms
    );
    score += bathroomMatch * 20;
    totalChecks += 20;

    // Square footage
    const sqftMatch = this.checkRangeMatch(
      featurePrefs.squareFootage,
      metadata.squareFootage
    );
    score += sqftMatch * 15;
    totalChecks += 15;

    // Amenities
    const amenityMatches = featurePrefs.amenities.filter(amenity =>
      this.checkAmenityMatch(amenity, propertyFeatures, metadata)
    ).length;
    score += (amenityMatches / featurePrefs.amenities.length) * 30;
    totalChecks += 30;

    // Must-haves
    const mustHaveMatches = featurePrefs.mustHaves.filter(mustHave =>
      this.checkAmenityMatch(mustHave, propertyFeatures, metadata)
    ).length;
    score += (mustHaveMatches / featurePrefs.mustHaves.length) * 15;
    totalChecks += 15;

    return totalChecks > 0 ? (score / totalChecks) * 100 : 50;
  }

  // Check if value is within range
  private static checkRangeMatch(range: { min: number; max: number }, value: number): number {
    if (value >= range.min && value <= range.max) {
      return 1; // Perfect match
    } else if (value < range.min) {
      return Math.max(0, 1 - (range.min - value) / range.min);
    } else {
      return Math.max(0, 1 - (value - range.max) / range.max);
    }
  }

  // Check if amenity is available
  private static checkAmenityMatch(
    amenity: string,
    features: Property['features'],
    metadata: Property['metadata']
  ): boolean {
    const amenityMap: { [key: string]: boolean } = {
      'pool': features.pool,
      'garden': features.garden,
      'balcony': features.balcony,
      'fireplace': features.fireplace,
      'basement': features.basement,
      'attic': features.attic,
      'garage': features.garage,
      'pet_friendly': metadata.petFriendly || false,
      'furnished': metadata.furnished || false,
      'smart_home': features.smartHome,
      'energy_efficient': features.energyEfficient,
    };

    return amenityMap[amenity] || false;
  }

  // Calculate timeline match score
  private static calculateTimelineScore(
    timeline: Client['preferences']['timeline']
  ): number {
    // This is a simplified calculation
    // In production, you'd consider move-in dates, listing dates, etc.
    const urgency = timeline.urgency;
    const flexibility = timeline.flexibility;

    let score = 50; // Base score

    // Higher urgency = higher score for available properties
    if (urgency === 'high') score += 30;
    else if (urgency === 'medium') score += 15;

    // Higher flexibility = higher score
    if (flexibility === 'high') score += 20;
    else if (flexibility === 'medium') score += 10;

    return Math.min(100, score);
  }

  // Generate match explanation
  private static generateMatchExplanation(
    score: number,
    strengths: string[],
    weaknesses: string[]
  ): string {
    if (score >= 90) {
      return `Excellent match! ${strengths.join(', ')}.`;
    } else if (score >= 80) {
      return `Great match! ${strengths.join(', ')}.`;
    } else if (score >= 70) {
      return `Good match. ${strengths.join(', ')}.`;
    } else if (score >= 60) {
      return `Decent match. ${strengths.join(', ')}. Consider: ${weaknesses.join(', ')}.`;
    } else {
      return `Limited match. ${weaknesses.join(', ')}.`;
    }
  }

  // Get matches for a client
  static async getMatchesForClient(clientId: string): Promise<Match[]> {
    try {
      const q = query(
        collection(db, this.collection),
        where('clientId', '==', clientId),
        orderBy('score.overall', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Match[];
    } catch (error) {
      throw new Error(`Failed to get matches for client: ${error}`);
    }
  }

  // Get matches for an agent
  static async getMatchesForAgent(agentId: string): Promise<Match[]> {
    try {
      const q = query(
        collection(db, this.collection),
        where('agentId', '==', agentId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Match[];
    } catch (error) {
      throw new Error(`Failed to get matches for agent: ${error}`);
    }
  }

  // Update match status (legacy - use updateMatchStatusWithHistory for new code)
  static async updateMatchStatus(matchId: string, status: MatchStatus): Promise<void> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated');
      }
      await this.updateMatchStatusWithHistory(matchId, status, currentUser.id);
    } catch (error: any) {
      throw new Error(`Failed to update match status: ${error.message || error}`);
    }
  }

  // Record match interaction
  static async recordMatchInteraction(
    matchId: string,
    interactionType: 'viewed' | 'clicked' | 'interested' | 'not_interested'
  ): Promise<void> {
    try {
      const match = await this.getMatch(matchId);
      if (!match) {
        throw new Error('Match not found');
      }

      const analytics = { ...match.analytics };
      
      switch (interactionType) {
        case 'viewed':
          analytics.clickThroughRate = (analytics.clickThroughRate + 1) / 2; // Simple average
          break;
        case 'clicked':
          analytics.conversionRate = (analytics.conversionRate + 1) / 2; // Simple average
          break;
        case 'interested':
          analytics.conversionRate = Math.min(100, analytics.conversionRate + 10);
          break;
        case 'not_interested':
          analytics.conversionRate = Math.max(0, analytics.conversionRate - 5);
          break;
      }

      await updateDoc(doc(db, this.collection, matchId), {
        analytics,
        updatedAt: new Date(),
      });
    } catch (error) {
      throw new Error(`Failed to record match interaction: ${error}`);
    }
  }

  // Get match by ID
  static async getMatch(matchId: string): Promise<Match | null> {
    try {
      const docRef = doc(db, this.collection, matchId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Match;
      }
      return null;
    } catch (error) {
      throw new Error(`Failed to get match: ${error}`);
    }
  }

  // Delete match
  static async deleteMatch(matchId: string): Promise<void> {
    try {
      const docRef = doc(db, this.collection, matchId);
      await deleteDoc(docRef);
    } catch (error) {
      throw new Error(`Failed to delete match: ${error}`);
    }
  }

  // Get all matches with pagination
  static async getMatches(
    page: number = 1,
    pageSize: number = 12
  ): Promise<ApiResponse<Match[]>> {
    try {
      const q = query(
        collection(db, this.collection),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );

      const querySnapshot = await getDocs(q);
      const matches = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Match[];

      const pagination: PaginationInfo = {
        page,
        limit: pageSize,
        total: matches.length,
        totalPages: Math.ceil(matches.length / pageSize),
        hasNext: page < Math.ceil(matches.length / pageSize),
        hasPrev: page > 1,
      };

      return {
        success: true,
        data: matches,
        pagination,
      };
    } catch (error) {
      return {
        success: false,
        error: `Failed to get matches: ${error}`,
      };
    }
  }

  /**
   * Get detailed match reasons/explanation
   * Returns why a property matches a client with detailed breakdown
   */
  static getMatchReasons(client: Client, property: Property): {
    explanation: string;
    strengths: string[];
    weaknesses: string[];
    scoreBreakdown: {
      category: string;
      score: number;
      weight: number;
      contribution: number;
    }[];
    recommendations: string[];
  } {
    const score = this.calculateMatchScore(client, property);
    
    const scoreBreakdown = [
      {
        category: 'Price',
        score: score.price,
        weight: 30,
        contribution: (score.price * 30) / 100,
      },
      {
        category: 'Location',
        score: score.location,
        weight: 25,
        contribution: (score.location * 25) / 100,
      },
      {
        category: 'Property Type',
        score: score.features, // Using features as type score
        weight: 20,
        contribution: (score.features * 20) / 100,
      },
      {
        category: 'Features',
        score: score.features,
        weight: 15,
        contribution: (score.features * 15) / 100,
      },
      {
        category: 'Timeline',
        score: score.timeline,
        weight: 10,
        contribution: (score.timeline * 10) / 100,
      },
    ];

    const recommendations: string[] = [];
    
    if (score.price < 70) {
      recommendations.push('Consider negotiating price or exploring financing options');
    }
    if (score.location < 70) {
      recommendations.push('Review location details and nearby amenities');
    }
    if (score.features < 70) {
      recommendations.push('Check if property can be modified to meet requirements');
    }
    if (score.overall >= 80) {
      recommendations.push('This is a strong match - consider scheduling a viewing');
    } else if (score.overall >= 60) {
      recommendations.push('This is a decent match - review details carefully');
    } else {
      recommendations.push('This match may not meet all requirements - consider alternatives');
    }

    return {
      explanation: score.explanation || '',
      strengths: score.strengths || [],
      weaknesses: score.weaknesses || [],
      scoreBreakdown,
      recommendations,
    };
  }

  /**
   * Track match history (status changes, interactions)
   */
  static async trackMatchHistory(
    matchId: string,
    event: {
      type: 'status_change' | 'viewed' | 'interaction' | 'note';
      previousValue?: any;
      newValue?: any;
      userId: string;
      notes?: string;
    }
  ): Promise<void> {
    try {
      const match = await this.getMatch(matchId);
      if (!match) {
        throw new Error('Match not found');
      }

      // Store history in match document metadata
      const history = (match as any).history || [];
      const historyEntry = {
        id: `history_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: event.type,
        previousValue: event.previousValue,
        newValue: event.newValue,
        userId: event.userId,
        notes: event.notes,
        timestamp: Timestamp.now(),
      };

      history.push(historyEntry);

      await updateDoc(doc(db, this.collection, matchId), {
        history: history.slice(-50), // Keep last 50 entries
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to track match history: ${error.message || error}`);
    }
  }

  /**
   * Get match history
   */
  static async getMatchHistory(matchId: string): Promise<any[]> {
    try {
      const match = await this.getMatch(matchId);
      if (!match) {
        throw new Error('Match not found');
      }

      return (match as any).history || [];
    } catch (error: any) {
      throw new Error(`Failed to get match history: ${error.message || error}`);
    }
  }

  /**
   * Get match analytics
   * Returns performance metrics for a specific match
   */
  static async getMatchAnalytics(matchId: string): Promise<{
    matchId: string;
    score: MatchScore;
    status: MatchStatus;
    clickThroughRate: number;
    conversionRate: number;
    timeToView?: number; // seconds
    timeToResponse?: number; // seconds
    viewCount: number;
    interactionCount: number;
    statusHistory: { status: MatchStatus; timestamp: Date }[];
    performance: 'excellent' | 'good' | 'average' | 'poor';
  }> {
    try {
      const match = await this.getMatch(matchId);
      if (!match) {
        throw new Error('Match not found');
      }

      const history = (match as any).history || [];
      const statusHistory = history
        .filter((h: any) => h.type === 'status_change')
        .map((h: any) => ({
          status: h.newValue,
          timestamp: h.timestamp?.toDate() || new Date(),
        }));

      const viewEvents = history.filter((h: any) => h.type === 'viewed');
      const interactionEvents = history.filter((h: any) => h.type === 'interaction');

      const timeToView = match.analytics?.viewedAt
        ? (match.analytics.viewedAt.toMillis() - (match.createdAt as any).toMillis()) / 1000
        : undefined;

      const timeToResponse = match.analytics?.respondedAt
        ? (match.analytics.respondedAt.toMillis() - (match.createdAt as any).toMillis()) / 1000
        : undefined;

      let performance: 'excellent' | 'good' | 'average' | 'poor' = 'average';
      if (match.score.overall >= 85 && match.analytics?.conversionRate >= 70) {
        performance = 'excellent';
      } else if (match.score.overall >= 75 && match.analytics?.conversionRate >= 50) {
        performance = 'good';
      } else if (match.score.overall >= 60 && match.analytics?.conversionRate >= 30) {
        performance = 'average';
      } else {
        performance = 'poor';
      }

      return {
        matchId: match.id,
        score: match.score,
        status: match.status,
        clickThroughRate: match.analytics?.clickThroughRate || 0,
        conversionRate: match.analytics?.conversionRate || 0,
        timeToView,
        timeToResponse,
        viewCount: viewEvents.length,
        interactionCount: interactionEvents.length,
        statusHistory,
        performance,
      };
    } catch (error: any) {
      throw new Error(`Failed to get match analytics: ${error.message || error}`);
    }
  }

  /**
   * Get match performance metrics (aggregated)
   */
  static async getMatchPerformanceMetrics(filters?: {
    agentId?: string;
    clientId?: string;
    dateRange?: { start: Date; end: Date };
  }): Promise<{
    totalMatches: number;
    averageScore: number;
    statusDistribution: { status: MatchStatus; count: number; percentage: number }[];
    averageConversionRate: number;
    averageClickThroughRate: number;
    topPerformingMatches: Match[];
    conversionFunnel: {
      created: number;
      viewed: number;
      interested: number;
      scheduled: number;
      closed: number;
    };
  }> {
    try {
      let q = query(collection(db, this.collection));

      if (filters?.agentId) {
        q = query(q, where('agentId', '==', filters.agentId));
      }
      if (filters?.clientId) {
        q = query(q, where('clientId', '==', filters.clientId));
      }

      const querySnapshot = await getDocs(q);
      let matches = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Match[];

      // Filter by date range if provided
      if (filters?.dateRange) {
        matches = matches.filter(match => {
          const createdAt = (match.createdAt as any).toDate();
          return createdAt >= filters.dateRange!.start && createdAt <= filters.dateRange!.end;
        });
      }

      const totalMatches = matches.length;
      const averageScore = matches.length > 0
        ? matches.reduce((sum, m) => sum + (m.score?.overall || 0), 0) / matches.length
        : 0;

      const statusCounts = new Map<MatchStatus, number>();
      matches.forEach(m => {
        statusCounts.set(m.status, (statusCounts.get(m.status) || 0) + 1);
      });

      const statusDistribution = Array.from(statusCounts.entries()).map(([status, count]) => ({
        status,
        count,
        percentage: (count / totalMatches) * 100,
      }));

      const averageConversionRate = matches.length > 0
        ? matches.reduce((sum, m) => sum + (m.analytics?.conversionRate || 0), 0) / matches.length
        : 0;

      const averageClickThroughRate = matches.length > 0
        ? matches.reduce((sum, m) => sum + (m.analytics?.clickThroughRate || 0), 0) / matches.length
        : 0;

      const topPerformingMatches = matches
        .sort((a, b) => {
          const scoreA = a.score?.overall || 0;
          const scoreB = b.score?.overall || 0;
          const convA = a.analytics?.conversionRate || 0;
          const convB = b.analytics?.conversionRate || 0;
          return (scoreB + convB) - (scoreA + convA);
        })
        .slice(0, 10);

      const conversionFunnel = {
        created: matches.length,
        viewed: matches.filter(m => m.analytics?.viewedAt).length,
        interested: matches.filter(m => m.status === MatchStatus.INTERESTED).length,
        scheduled: matches.filter(m => m.status === MatchStatus.SCHEDULED).length,
        closed: matches.filter(m => false).length,
      };

      return {
        totalMatches,
        averageScore: Math.round(averageScore),
        statusDistribution,
        averageConversionRate: Math.round(averageConversionRate),
        averageClickThroughRate: Math.round(averageClickThroughRate),
        topPerformingMatches,
        conversionFunnel,
      };
    } catch (error: any) {
      throw new Error(`Failed to get match performance metrics: ${error.message || error}`);
    }
  }

  /**
   * Update match status with history tracking
   */
  static async updateMatchStatusWithHistory(
    matchId: string,
    newStatus: MatchStatus,
    userId: string
  ): Promise<void> {
    try {
      const match = await this.getMatch(matchId);
      if (!match) {
        throw new Error('Match not found');
      }

      const previousStatus = match.status;

      await updateDoc(doc(db, this.collection, matchId), {
        status: newStatus,
        updatedAt: Timestamp.now(),
      });

      // Track history
      await this.trackMatchHistory(matchId, {
        type: 'status_change',
        previousValue: previousStatus,
        newValue: newStatus,
        userId,
      });
    } catch (error: any) {
      throw new Error(`Failed to update match status: ${error.message || error}`);
    }
  }
}
